"use client";

import { c as _c } from "react/compiler-runtime";
import { QueryClient, QueryClientProvider, keepPreviousData } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import type { AuthChangeEvent, Session } from "@supabase/supabase-js";
import { appendClientDebugAction } from "@/lib/debug/client-debug";
import { createClient } from "@/lib/supabase/client";
import { clearUserLocalStorage } from "@/lib/auth/clear-user-local-storage";
import { clearAllAccountCache, clearAllRoutinesCache, clearWorkoutCache } from "@/lib/sync/workout-cache";
import { getCurrentStorageScope, getScopedStorageKeyForUser } from "@/lib/sync/storage-scope";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/components/theme-provider";
import { APP_STARTUP_READY_EVENT, AppStartupScreen } from "@/components/startup/app-startup-screen";
import { persistReportDebugStartupTimelineEvent } from "@/lib/debug/reportdebug-route-context";
const CACHE_RESTORE_TIMEOUT_MS = 4_000;
const QUERY_CACHE_STORAGE_KEY = "coaching-app-cache";
const CLIENT_QUERY_ROOT = "client";
type CacheRestoreStatus = "idle" | "pending" | "success" | "error" | "timeout";
type StartupOverlayWindow = Window & {
  __hideUltrackStartup?: (reason?: string) => void;
};
function serializeError(error: unknown) {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message
    };
  }
  return {
    message: typeof error === "string" ? error : "unknown"
  };
}
export function Providers({
  children
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const [cacheRestoreStatus, setCacheRestoreStatus] = useState<CacheRestoreStatus>("idle");
  const [isMounted, setIsMounted] = useState(false);
  const [persistKey, setPersistKey] = useState<string | null>(null);
  const startupReadyDispatchedRef = useRef(false);
  const cacheRestoreStartedAtRef = useRef<number | null>(null);
  const authUserIdRef = useRef<string | null>(null);
  const mountStartedAtRef = useRef(Date.now());
  const cacheRestoreStatusRef = useRef(cacheRestoreStatus);
  cacheRestoreStatusRef.current = cacheRestoreStatus;
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        // Keep data fresh for 5 minutes - instant navigation between tabs
        staleTime: 5 * 60 * 1000,
        // 5 minutes
        // Keep unused data in cache for 24 hours (for offline support)
        gcTime: 24 * 60 * 60 * 1000,
        // 24 hours
        // Refetch when user returns to app
        refetchOnWindowFocus: true,
        // Refetch when network reconnects
        refetchOnReconnect: true,
        // Retry failed requests
        retry: 2,
        // Show previous data while refetching - instant perceived navigation
        placeholderData: keepPreviousData
      }
    }
  }));

  // Create persister lazily - only on client side after mount
  const [persister, setPersister] = useState<ReturnType<typeof createSyncStoragePersister> | null>(null);
  function rebuildPersistedQueryScope(userId: string | null = authUserIdRef.current) {
    const nextPersistKey = getScopedStorageKeyForUser(QUERY_CACHE_STORAGE_KEY, userId);
    const prevPersistKey = persistKey;
    appendClientDebugAction({
      source: "providers",
      event: "startup-cache-rebuild-scope",
      details: {
        nextUserId: userId,
        prevPersistKey,
        nextPersistKey,
        isMounted,
        cacheRestoreStatus
      }
    });
    persistReportDebugStartupTimelineEvent({
      source: "providers",
      event: "startup-cache-rebuild-scope",
      details: {
        nextUserId: userId,
        prevPersistKey,
        nextPersistKey,
        isMounted,
        cacheRestoreStatus
      }
    });
    setPersister(null);
    setCacheRestoreStatus("idle");
    cacheRestoreStartedAtRef.current = null;
    setPersistKey(nextPersistKey);
  }
  function clearPersistedQueryCacheForUser(userId_0: string | null) {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.removeItem(getScopedStorageKeyForUser(QUERY_CACHE_STORAGE_KEY, userId_0));
      window.localStorage.removeItem(QUERY_CACHE_STORAGE_KEY);
    } catch {
      // Best-effort cache cleanup only.
    }
  }

  // Establish the initial cache scope after mount to avoid hydration mismatch.
  useEffect(() => {
    const mountStartedMs = mountStartedAtRef.current;
    persistReportDebugStartupTimelineEvent({
      source: "providers",
      event: "providers-mounted",
      details: {
        effectAtIso: new Date().toISOString()
      }
    });
    const rafId = window.requestAnimationFrame(() => {
      persistReportDebugStartupTimelineEvent({
        source: "providers",
        event: "providers-mount-raf",
        details: {
          userIdPresent: Boolean(getCurrentStorageScope().userId)
        }
      });
      setIsMounted(true);
      authUserIdRef.current = getCurrentStorageScope().userId;
      setPersistKey(getScopedStorageKeyForUser(QUERY_CACHE_STORAGE_KEY, authUserIdRef.current));
    });
    return () => {
      window.cancelAnimationFrame(rafId);
      persistReportDebugStartupTimelineEvent({
        source: "providers",
        event: "providers-unmounted",
        details: {
          effectAtIso: new Date().toISOString(),
          mountedDurationMs: Math.max(0, Date.now() - mountStartedMs),
          isMounted,
          hasPersister: !!persister,
          cacheRestoreStatus: cacheRestoreStatusRef.current
        }
      });
    };
  }, []);

  // Initialize the scoped persister after mount.
  useEffect(() => {
    if (!isMounted || !persistKey) return;
    startupReadyDispatchedRef.current = false;
    appendClientDebugAction({
      source: "providers",
      event: "startup-cache-restore-init",
      details: {
        key: persistKey
      }
    });
    persistReportDebugStartupTimelineEvent({
      source: "providers",
      event: "startup-cache-restore-init",
      details: {
        key: persistKey
      }
    });
    try {
      const basePersister = createSyncStoragePersister({
        storage: window.localStorage,
        key: persistKey
      });
      const instrumentedPersister: ReturnType<typeof createSyncStoragePersister> = {
        persistClient: async persistedClient => {
          try {
            await basePersister.persistClient(persistedClient);
          } catch (error_0) {
            appendClientDebugAction({
              source: "providers",
              event: "startup-cache-persist-error",
              details: serializeError(error_0)
            });
            throw error_0;
          }
        },
        restoreClient: async () => {
          cacheRestoreStartedAtRef.current = Date.now();
          appendClientDebugAction({
            source: "providers",
            event: "startup-cache-restore-start"
          });
          persistReportDebugStartupTimelineEvent({
            source: "providers",
            event: "startup-cache-restore-start"
          });
          try {
            const restored = await basePersister.restoreClient();
            appendClientDebugAction({
              source: "providers",
              event: "startup-cache-restore-read",
              details: {
                hasPersistedClient: !!restored,
                persistedAgeMs: restored?.timestamp != null ? Math.max(0, Date.now() - restored.timestamp) : null
              }
            });
            return restored;
          } catch (error_1) {
            appendClientDebugAction({
              source: "providers",
              event: "startup-cache-restore-read-error",
              details: serializeError(error_1)
            });
            throw error_1;
          }
        },
        removeClient: async () => {
          try {
            await basePersister.removeClient();
            appendClientDebugAction({
              source: "providers",
              event: "startup-cache-restore-cleared"
            });
          } catch (error_2) {
            appendClientDebugAction({
              source: "providers",
              event: "startup-cache-clear-error",
              details: serializeError(error_2)
            });
            throw error_2;
          }
        }
      };
      setPersister(instrumentedPersister);
      setCacheRestoreStatus("pending");
    } catch (error) {
      appendClientDebugAction({
        source: "providers",
        event: "startup-cache-persister-create-error",
        details: serializeError(error)
      });
      setCacheRestoreStatus("error");
    }
  }, [isMounted, persistKey]);

  // Clear client query state and move to the correct persisted cache namespace when
  // the authenticated browser user changes.
  useEffect(() => {
    if (!isMounted) return;
    const supabase = createClient();
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((_event: AuthChangeEvent, session: Session | null) => {
      const nextUserId = session?.user?.id ?? null;
      const previousUserId = authUserIdRef.current;
      if (previousUserId === nextUserId) return;

      // On first mount, INITIAL_SESSION fires before the mount-effect RAF has
      // resolved the real userId from localStorage. Treat this as initialization
      // rather than a scope change to avoid <>rebuildPersistedQueryScope</> flashing
      // AppStartupScreen during an otherwise routine page load.
      if (_event === "INITIAL_SESSION" && previousUserId === null) {
        authUserIdRef.current = nextUserId;
        return;
      }
      appendClientDebugAction({
        source: "providers",
        event: "startup-cache-auth-scope-changed",
        details: {
          authEvent: _event,
          previousUserIdPresent: Boolean(previousUserId),
          nextUserIdPresent: Boolean(nextUserId)
        }
      });
      authUserIdRef.current = nextUserId;
      appendClientDebugAction({
        source: "providers",
        event: "startup-cache-query-client-cleared",
        details: {
          authEvent: _event,
          previousUserIdPresent: Boolean(previousUserId),
          nextUserIdPresent: Boolean(nextUserId),
          queryCountBeforeClear: queryClient.getQueryCache().getAll().length,
          mutationCountBeforeClear: queryClient.getMutationCache().getAll().length
        }
      });
      persistReportDebugStartupTimelineEvent({
        source: "providers",
        event: "startup-cache-query-client-cleared",
        details: {
          authEvent: _event,
          previousUserIdPresent: Boolean(previousUserId),
          nextUserIdPresent: Boolean(nextUserId),
          queryCountBeforeClear: queryClient.getQueryCache().getAll().length,
          mutationCountBeforeClear: queryClient.getMutationCache().getAll().length
        }
      });
      queryClient.clear();
      if (!nextUserId) {
        clearPersistedQueryCacheForUser(previousUserId);
        clearPersistedQueryCacheForUser(null);
        clearUserLocalStorage();
        void Promise.all([clearWorkoutCache(), clearAllRoutinesCache(), clearAllAccountCache()]).catch(() => undefined);
      } else if (previousUserId !== null) {
        // Different user signed in without explicit sign-out — clear the
        // previous user's saved route so the ClientShell restoration effect
        // doesn't redirect a different user to the wrong page.
        if (typeof window !== "undefined") {
          localStorage.removeItem("clientSavedRoute");
          localStorage.removeItem("clientSavedRouteHref");
          localStorage.removeItem("clientSavedRouteTimestamp");
        }
      }
      rebuildPersistedQueryScope(nextUserId);
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [isMounted, queryClient]);
  useEffect(() => {
    if (!persister) return;
    if (cacheRestoreStatus !== "pending") return;
    const timeoutId = window.setTimeout(() => {
      setCacheRestoreStatus(currentStatus => {
        if (currentStatus !== "pending") return currentStatus;
        appendClientDebugAction({
          source: "providers",
          event: "startup-cache-restore-timeout",
          details: {
            timeoutMs: CACHE_RESTORE_TIMEOUT_MS,
            elapsedMs: cacheRestoreStartedAtRef.current != null ? Math.max(0, Date.now() - cacheRestoreStartedAtRef.current) : null
          }
        });
        persistReportDebugStartupTimelineEvent({
          source: "providers",
          event: "startup-cache-restore-timeout",
          details: {
            timeoutMs: CACHE_RESTORE_TIMEOUT_MS,
            elapsedMs: cacheRestoreStartedAtRef.current != null ? Math.max(0, Date.now() - cacheRestoreStartedAtRef.current) : null
          }
        });
        return "timeout";
      });
    }, CACHE_RESTORE_TIMEOUT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [cacheRestoreStatus, persister]);
  useEffect(() => {
    if (startupReadyDispatchedRef.current) return;
    if (!isMounted) return;
    if (persister && cacheRestoreStatus === "pending") return;
    startupReadyDispatchedRef.current = true;
    const emitReady = (reason: string) => {
      const hideStartupOverlay = (window as StartupOverlayWindow).__hideUltrackStartup;
      const details = {
        cacheRestoreStatus,
        hasPersister: !!persister,
        reason,
        hasOverlayHideFallback: typeof hideStartupOverlay === "function"
      };
      appendClientDebugAction({
        source: "providers",
        event: "startup-ready-dispatch-attempt",
        details
      });
      persistReportDebugStartupTimelineEvent({
        source: "providers",
        event: "startup-ready-dispatch-attempt",
        details
      });
      window.dispatchEvent(new Event(APP_STARTUP_READY_EVENT));
      hideStartupOverlay?.(`providers-ready-${reason}`);
    };
    appendClientDebugAction({
      source: "providers",
      event: "startup-ready-dispatched",
      details: {
        cacheRestoreStatus,
        hasPersister: !!persister
      }
    });
    persistReportDebugStartupTimelineEvent({
      source: "providers",
      event: "startup-ready-dispatched",
      details: {
        cacheRestoreStatus,
        hasPersister: !!persister
      }
    });
    emitReady("immediate");
    const rafId_0 = window.requestAnimationFrame(() => emitReady("raf"));
    const retryTimeoutId = window.setTimeout(() => emitReady("retry-250ms"), 250);
    return () => {
      window.cancelAnimationFrame(rafId_0);
      window.clearTimeout(retryTimeoutId);
    };
  }, [cacheRestoreStatus, isMounted, persister]);

  // Public marketing routes (e.g. /get) are static, SEO-facing pages that don't
  // need the offline query cache, sync, or the app startup splash. Render them
  // server-side (bypassing the isMounted gate) so their copy and structured
  // data ship in the initial HTML for crawlers and social scrapers, instead of
  // being client-rendered. App routes are untouched — they fall through below.
  if (pathname === "/get") {
    return <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          {children}
          <Toaster />
        </ThemeProvider>
      </QueryClientProvider>;
  }

  // Keep the startup shell visible until the provider is ready to show the app.
  if (!isMounted || !persister) {
    return <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AppStartupScreen />
          <Toaster />
        </ThemeProvider>
      </QueryClientProvider>;
  }

  // Use PersistQueryClientProvider when persister is available
  return <PersistQueryClientProvider client={queryClient} persistOptions={{
    persister,
    maxAge: 24 * 60 * 60 * 1000,
    // 24 hours
    dehydrateOptions: {
      shouldDehydrateQuery: query => {
        // Don't persist queries that are fetching or have errors
        return query.state.status === "success";
      }
    }
  }} onSuccess={() => {
    const clientQueryCountAtRestore = queryClient.getQueryCache().getAll().filter(query_0 => Array.isArray(query_0.queryKey) && query_0.queryKey[0] === CLIENT_QUERY_ROOT).length;
    if (authUserIdRef.current) {
      appendClientDebugAction({
        source: "providers",
        event: "startup-cache-client-query-invalidate",
        details: {
          authUserIdPresent: true,
          clientQueryCountAtRestore
        }
      });
      void queryClient.invalidateQueries({
        predicate: query_1 => Array.isArray(query_1.queryKey) && query_1.queryKey[0] === CLIENT_QUERY_ROOT
      });
    }
    appendClientDebugAction({
      source: "providers",
      event: "startup-cache-restore-success",
      details: {
        elapsedMs: cacheRestoreStartedAtRef.current != null ? Math.max(0, Date.now() - cacheRestoreStartedAtRef.current) : null,
        authUserIdPresent: Boolean(authUserIdRef.current),
        clientQueryCountAtRestore
      }
    });
    persistReportDebugStartupTimelineEvent({
      source: "providers",
      event: "startup-cache-restore-success",
      details: {
        elapsedMs: cacheRestoreStartedAtRef.current != null ? Math.max(0, Date.now() - cacheRestoreStartedAtRef.current) : null,
        authUserIdPresent: Boolean(authUserIdRef.current),
        clientQueryCountAtRestore
      }
    });
    setCacheRestoreStatus("success");
  }} onError={() => {
    appendClientDebugAction({
      source: "providers",
      event: "startup-cache-restore-error",
      details: {
        elapsedMs: cacheRestoreStartedAtRef.current != null ? Math.max(0, Date.now() - cacheRestoreStartedAtRef.current) : null
      }
    });
    persistReportDebugStartupTimelineEvent({
      source: "providers",
      event: "startup-cache-restore-error",
      details: {
        elapsedMs: cacheRestoreStartedAtRef.current != null ? Math.max(0, Date.now() - cacheRestoreStartedAtRef.current) : null
      }
    });
    setCacheRestoreStatus("error");
  }}>
      <ThemeProvider>
        {cacheRestoreStatus === "pending" ? <CacheRestoreShell /> : children}
        <Toaster />
      </ThemeProvider>
    </PersistQueryClientProvider>;
}

// Minimal loading shell shown while cache restores from localStorage
function CacheRestoreShell() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <AppStartupScreen />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}